/* === BEGIN imported: constants/steps.js (verbatim from plugin) === */
/**
 * Modal Step Constants
 * Eliminates magic strings for step navigation throughout the app
 */

export const MODAL_STEPS = {
  // Passenger Flow
  PASSENGER_QUANTITY: 'passengerQuantity',
  CHILD_SEATS_DETAILS: 'childSeatsDetails',
  PASSENGER_LUGGAGE: 'passengerLuggage',
  OVER_SIZE_LUGGAGE: 'overSizeLuggage',

  // Date/Time Flow
  TRAVELING_DATE: 'travelingDate',
  TRAVELING_DATE_TIME: 'travelingDateTime',
  TRAVELING_TIME: 'travelingTime',
  SMART_TIME_SELECT: 'smartTimeSelect',
  // Recommended-pickup pair reached from travelingDateTime's "Get a Recommended
  // Pickup Time" card: a 3-way chooser (departure time / flight number / manual)
  // followed by the result + "Why this pickup time?" breakdown.
  SMART_PICKUP_RECOMMENDATION: 'smartPickupRecommendation',
  SMART_PICKUP_RESULT: 'smartPickupResult',

  // Vehicle Selection
  CAR_SELECTION: 'carSelection',
  SELECTED_CAR_DETAILS: 'selectedCarDetails',
  POPULAR_QUESTIONS: 'popularQuestions',
  PREBOOK_COMPARE: 'prebookCompare',
  WHY_CHOOSE_US: 'whyChooseUs',
  // Cancellation / Modification / Refund tabbed info popup (desktop .upper-modal
  // in selectedCarDetailsLatest + orderDetails; mobile = this stacked overlay step).
  CANCELLATION_MODIFICATION_REFUND: 'cancellationModificationRefund',

  // Special Requests
  SPECIAL_REQUEST: 'specialRequest',

  // Airport/Flight Flow
  FLIGHT_AIRPORT_DETAIL: 'flightAirportDetail',
  FLIGHT_AIRPORT_TO: 'flightAirportTo',
  FLIGHT_AIRPORT_LISTS: 'flightAirportLists',
  FLIGHT_AIRLINES: 'flightAirlines',

  // Pricing
  COST_CALCULATION: 'costCalculation',
  PAYMENT_BREAKDOWN: 'paymentBreakDown',
  APPLY_COUPON: 'applyCoupon',

  // Payment Flow
  PAYMENT_CARDS: 'paymentCards',
  // "Personal Card" tab in selectedCarDetailsLatest — same Secure Payment content,
  // desktop popup / mobile stacked-overlay step (in POPUP_STEPS).
  PERSONAL_CARD: 'personalCard',
  INTEREST_FREE_PAYMENT: 'interestFreePayment',
  AFTER_PAY: 'afterPay',
  NO_SURPRISES: 'noSurprises',

  // Order Summary
  ORDER_DETAILS: 'orderDetails',

  // Booking Status
  PROCESSING_BOOKING: 'processingBooking',
  // Asks for the field(s) the booking gate still needs (in practice the phone —
  // Google / Apple never supply one) before payment, or after a refused booking.
  COMPLETE_PROFILE: 'completeProfile',
  BOOKING_RECEIVED: 'bookingReceived',
  DOWNLOAD_APP_REMINDER: 'downloadAppReminder',

  // Pay After Drop-off
  PAY_AFTER_DROPOFF: 'payAfterDropoff',

  // Policies
  MAIN_POLICIES: 'mainPolicies',
  POLICIES_LISTING: 'policiesListing',
  POLICIES_EXTRA_DETAILS: 'policiesExtraDetails',
  PASSENGER_POLICIES: 'passengerPolicies',
  DRIVER_POLICIES: 'driverPolicies',
  PICKUP_POLICIES: 'pickupPolicies',
  CANCELLATION_POLICIES: 'cancellationPolicies',
  REFUND_POLICIES: 'refundPolicies',
  TOLL_POLICIES: 'tollPolicies',
  FINDING_DRIVER_POLICIES: 'findingDriverPolicies',
  PRIVACY_POLICY: 'privacyPolicy',
  TERM_OF_SERVICES: 'termOfServices',

  // Airport Perks
  AIRPORT_PICKUP_PERKS: 'airportPickupPerks',
  AIRPORT_PICKUP_FEE_INFO: 'airportPickupFeeInfo',
  FLIGHT_DELAYED: 'flightDelayed',
  WAITING_TIME_INFO: 'waitingTimeInfo',
  FINDING_DRIVER: 'findingDriver',

  // Smart-time recommendation
  RECOMMENDED_PICKUP_DETAILS: 'recommendedPickupDetails',
  LIABILITY_DISCLAIMER: 'liabilityDisclaimer',
  SMART_PICKUP_WHY: 'smartPickupWhy',
  // travelingDateTime (fromAirport) — "Typical Exit Time After Landing" info icon.
  EXIT_TIME_INFO: 'exitTimeInfo',
};

/**
 * Legacy step name mapping for backward compatibility
 *
 * Maps old (typo) names to new corrected names. The misspelled keys below are
 * DELIBERATE and must stay: no code passes them any more (the codebase spells it
 * "policies" throughout), but they can still arrive from a returning visitor's
 * persisted sessionStorage step. Removing them would strand those sessions.
 */
const LEGACY_STEP_MAP = {
  // Typo fixes: spacial -> special
  'spacialRequest': MODAL_STEPS.SPECIAL_REQUEST,

  // Typo fixes: polices -> policies
  'policesListing': MODAL_STEPS.POLICIES_LISTING,
  'policesExtraDetails': MODAL_STEPS.POLICIES_EXTRA_DETAILS,
  'passengerPolices': MODAL_STEPS.PASSENGER_POLICIES,
  'driverPolices': MODAL_STEPS.DRIVER_POLICIES,
  'pickupPolices': MODAL_STEPS.PICKUP_POLICIES,
  'cancellationPolices': MODAL_STEPS.CANCELLATION_POLICIES,
  'refundPolices': MODAL_STEPS.REFUND_POLICIES,
  'tollPolices': MODAL_STEPS.TOLL_POLICIES,
  'findingDriverPolices': MODAL_STEPS.FINDING_DRIVER_POLICIES,
  'mainPolices': MODAL_STEPS.MAIN_POLICIES,

  // Payment merges: paymentType + paymentCard → paymentCards
  'paymentType': MODAL_STEPS.PAYMENT_CARDS,
  'paymentCard': MODAL_STEPS.PAYMENT_CARDS,

  // Date/Time merges: travelingDate + travelingTime → travelingDateTime
  'travelingDate': MODAL_STEPS.TRAVELING_DATE_TIME,
  'travelingTime': MODAL_STEPS.TRAVELING_DATE_TIME,

  // Pricing/payment breakdown alias — callers use 'pricingBreakdown'
  'pricingBreakdown': MODAL_STEPS.PAYMENT_BREAKDOWN,

  // selectedCarDetails now renders the ported SelectedCarDetailsLatest step
  // (no longer collapsed into carSelection).
};

/**
 * Normalize a step name - handles both legacy and current names
 * @param {string} step - The step name to normalize
 * @returns {string} - The normalized step name
 */
export function normalizeStep(step) {
  return LEGACY_STEP_MAP[step] || step;
}

/**
 * Steps that must NOT be persisted to LATEST_STEP — the booking-in-progress step
 * plus post-booking terminal/perk views. Persisting them lets a reload restore a
 * step whose backing data is gone (or, for processingBooking, re-fire the booking).
 * Compared against normalizeStep(step) so legacy spellings match.
 */
export const NON_PERSISTED_STEPS = [
  MODAL_STEPS.PROCESSING_BOOKING,
  MODAL_STEPS.BOOKING_RECEIVED,
  MODAL_STEPS.DOWNLOAD_APP_REMINDER,
  MODAL_STEPS.FINDING_DRIVER,
  MODAL_STEPS.FLIGHT_DELAYED,
  MODAL_STEPS.AIRPORT_PICKUP_PERKS,
  MODAL_STEPS.CANCELLATION_POLICIES,
  // Its rows live in a module-level store, not storage — nothing to restore after a reload.
  MODAL_STEPS.SMART_PICKUP_WHY,
  MODAL_STEPS.EXIT_TIME_INFO,
];

/**
 * Steps that are nested "popup" content rather than main-flow screens. On MOBILE,
 * navigating to one opens it as a stacked modal overlay (rendered by HopnModal) on
 * top of the current screen instead of replacing it. Desktop is unaffected (it uses
 * inline `.upper-modal` popups). Only info / perk / policy overlays belong here.
 */
export const POPUP_STEPS = new Set([
  MODAL_STEPS.CHILD_SEATS_DETAILS,
  MODAL_STEPS.RECOMMENDED_PICKUP_DETAILS,
  MODAL_STEPS.PREBOOK_COMPARE,
  MODAL_STEPS.WHY_CHOOSE_US,
  MODAL_STEPS.POPULAR_QUESTIONS,
  MODAL_STEPS.CANCELLATION_MODIFICATION_REFUND,
  MODAL_STEPS.SPECIAL_REQUEST,
  MODAL_STEPS.PAYMENT_BREAKDOWN,
  MODAL_STEPS.PERSONAL_CARD,
  MODAL_STEPS.APPLY_COUPON,
  MODAL_STEPS.AFTER_PAY,
  MODAL_STEPS.NO_SURPRISES,
  MODAL_STEPS.INTEREST_FREE_PAYMENT,
  MODAL_STEPS.PAY_AFTER_DROPOFF,
  MODAL_STEPS.MAIN_POLICIES,
  MODAL_STEPS.POLICIES_LISTING,
  MODAL_STEPS.POLICIES_EXTRA_DETAILS,
  MODAL_STEPS.PASSENGER_POLICIES,
  MODAL_STEPS.DRIVER_POLICIES,
  MODAL_STEPS.PICKUP_POLICIES,
  MODAL_STEPS.CANCELLATION_POLICIES,
  MODAL_STEPS.REFUND_POLICIES,
  MODAL_STEPS.TOLL_POLICIES,
  MODAL_STEPS.FINDING_DRIVER_POLICIES,
  MODAL_STEPS.PRIVACY_POLICY,
  MODAL_STEPS.TERM_OF_SERVICES,
  MODAL_STEPS.AIRPORT_PICKUP_PERKS,
  MODAL_STEPS.AIRPORT_PICKUP_FEE_INFO,
  MODAL_STEPS.FLIGHT_DELAYED,
  MODAL_STEPS.WAITING_TIME_INFO,
  MODAL_STEPS.FINDING_DRIVER,
  MODAL_STEPS.COST_CALCULATION,
  MODAL_STEPS.LIABILITY_DISCLAIMER,
  MODAL_STEPS.SMART_PICKUP_WHY,
  MODAL_STEPS.EXIT_TIME_INFO,
]);

export default MODAL_STEPS;
/* === END imported: constants/steps.js === */
